import React, { useEffect, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useMoodleV2 } from '../../contexts/moodle';
import { useTheme } from '../../contexts/ThemeContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import BroadcastComposerContainer from './BroadcastComposerContainer';
import GroupComposerContainer from './GroupComposerContainer';
import GroupManagerContainer from './GroupManagerContainer';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { motion, AnimatePresence } from 'framer-motion';
import ChatSection from './ChatSection';
import {
  Search,
  GraduationCap,
  Users,
  X,
  ChevronRight,
  ArrowLeft,
  Megaphone,
  MoreVertical,
  Pencil,
  Plus,
  MessageSquare,
  UserPlus
} from 'lucide-react';

const ChatContainer = () => {
  const {
    contacts,
    broadcastConversations,
    groupConversations,
    canBroadcast,
    canCreateGroup,
    selectedContact,
    messages,
    loading,
    error,
    selectContact,
    sendChatMessage,
    closeConversation
  } = useChat();

  const moodle = useMoodleV2();
  const theme = useTheme();
  const courseId = moodle.currentCourse?.id;
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  // Extra views swapped into the existing AnimatePresence, alongside the
  // contacts list and the open chat window — not a modal/overlay.
  const [showComposer, setShowComposer] = useState(false);
  const [showGroupComposer, setShowGroupComposer] = useState(false);
  // Group currently being edited (rename + membership). Holds the row object
  // itself, not just an id, so the manager screen starts from the membership
  // the list already fetched instead of waiting on another round-trip.
  const [managedGroup, setManagedGroup] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const SECTIONS_STORAGE_KEY = 'tipddy_chat_sections_v1';

  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem(SECTIONS_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      broadcasts: true,
      groups: true,
      tutors: true,
      classmates: true,
    };
  });

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  };

  const [showAllClassmates, setShowAllClassmates] = useState(false);

  const isDark = theme?.mode === 'dark';

  // Theme-driven styling values
  const primaryColor = theme?.colors?.primary_light || '#1a4275';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const containerBg = isDark ? '#111e35' : '#ffffff';
  const borderCol = isDark ? '#1e293b' : '#e2e8f0';
  const textPrimary = isDark ? '#f8fafc' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const scrollbarThumb = isDark ? '#334155' : '#cbd5e1';
  const scrollbarThumbHover = isDark ? '#475569' : '#94a3b8';

  // The shared ChatProvider (mounted above this component) owns loading data
  // for the active course and resets session state on course change. This
  // component only needs to close the open conversation when it unmounts —
  // the roster/unread poll keeps running so the floating button badge stays
  // accurate while the chat container is closed.
  useEffect(() => {
    return () => {
      closeConversation();
    };
  }, [closeConversation]);

  const handleSendMessage = async (text) => {
    await sendChatMessage(text);
  };

  const filteredContacts = contacts.filter(c =>
    c.fullname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tutors = filteredContacts.filter(c => c.istutor);
  const allClassmates = filteredContacts.filter(c => !c.istutor);

  // Active classmates: either have an active conversation or unread messages
  const activeClassmates = allClassmates.filter(c => c.conversationid !== null || c.unreadcount > 0);
  // Inactive classmates: in the course roster but no conversation started yet
  const inactiveClassmates = allClassmates.filter(c => c.conversationid === null && !c.unreadcount);

  // When searching, display all matched classmates. When not searching, display active ones (or all if expanded)
  const displayedClassmates = searchTerm.trim() !== ''
    ? allClassmates
    : (showAllClassmates ? allClassmates : activeClassmates);

  // Unread counts per section
  const broadcastUnread = broadcastConversations.reduce((sum, b) => sum + (Number(b.unreadcount) || 0), 0);
  const groupUnread = groupConversations.reduce((sum, g) => sum + (Number(g.unreadcount) || 0), 0);
  const tutorsUnread = tutors.reduce((sum, t) => sum + (Number(t.unreadcount) || 0), 0);
  const classmatesUnread = allClassmates.reduce((sum, c) => sum + (Number(c.unreadcount) || 0), 0);

  // Auto-expand sections when new unread messages arrive
  useEffect(() => {
    if (broadcastUnread > 0 && !openSections.broadcasts) {
      setOpenSections(prev => ({ ...prev, broadcasts: true }));
    }
    if (groupUnread > 0 && !openSections.groups) {
      setOpenSections(prev => ({ ...prev, groups: true }));
    }
  }, [broadcastUnread, groupUnread]);

  // Helper to render premium initials avatar if image is missing or default
  const renderPremiumAvatar = (contact, size = 44) => {
    const hasPhoto = contact.profileimageurl && 
                     !contact.profileimageurl.includes('f1.png') && 
                     !contact.profileimageurl.includes('f1.svg') && 
                     !contact.profileimageurl.includes('f2.png') && 
                     !contact.profileimageurl.includes('f2.svg') && 
                     !contact.profileimageurl.includes('default');

    if (!hasPhoto) {
      const initials = contact.fullname 
        ? contact.fullname.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() 
        : '?';
      
      const gradient = contact.istutor
        ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`
        : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`;

      return (
        <Avatar
          style={{
            width: size,
            height: size,
            background: gradient,
            color: '#ffffff',
            fontSize: size === 44 ? '15px' : '13px',
            fontWeight: 'bold',
            border: `1.5px solid ${borderCol}`
          }}
        >
          {initials}
        </Avatar>
      );
    }

    return (
      <Avatar 
        src={contact.profileimageurl} 
        alt={contact.fullname}
        style={{ 
          width: size, 
          height: size,
          border: `1.5px solid ${borderCol}`
        }}
      />
    );
  };

  return (
    <div 
      className={`${
        isMobile 
          ? "fixed inset-0 z-[9999] w-screen h-screen flex flex-col" 
          : "flex h-full flex-1 overflow-hidden relative w-full"
      } animate-fade-in`}
      style={{ 
        backgroundColor: containerBg,
        fontFamily: "'Outfit', 'Montserrat', sans-serif",
        '--scrollbar-thumb-color': scrollbarThumb,
        '--scrollbar-thumb-hover-color': scrollbarThumbHover
      }}
    >
      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--scrollbar-thumb-color, #cbd5e1);
          border-radius: 99px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--scrollbar-thumb-hover-color, #94a3b8);
        }
      `}} />

      <AnimatePresence mode="wait">
        {showComposer ? (
          /* VISTA 3: Composer de Anuncios (broadcast) */
          <motion.div
            key="broadcast-composer"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col h-full overflow-hidden"
          >
            <BroadcastComposerContainer
              courseId={courseId}
              onBack={() => setShowComposer(false)}
            />
          </motion.div>
        ) : managedGroup ? (
          /* VISTA 5: Gestión de un grupo existente (renombrar + miembros) */
          <motion.div
            key="group-manager"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col h-full overflow-hidden"
          >
            <GroupManagerContainer
              group={managedGroup}
              courseId={courseId}
              onBack={() => setManagedGroup(null)}
            />
          </motion.div>
        ) : showGroupComposer ? (
          /* VISTA 4: Composer de Grupos (bidireccional) */
          <motion.div
            key="group-composer"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col h-full overflow-hidden"
          >
            <GroupComposerContainer
              courseId={courseId}
              onBack={() => setShowGroupComposer(false)}
            />
          </motion.div>
        ) : !selectedContact ? (
          /* VISTA 1: Lista de Contactos (100% de ancho) */
          <motion.div
            key="contacts-list"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col h-full"
          >
            {/* Header de Mensajería (solo visible en Móviles) */}
            {isMobile && (
              <div 
                className="p-4 flex items-center justify-between border-b"
                style={{ 
                  borderColor: borderCol,
                  backgroundColor: isDark ? '#162542' : '#ffffff'
                }}
              >
                <h3 className="font-bold text-2xl m-0" style={{ color: textPrimary }}>Mensajería</h3>
                <button 
                  onClick={() => moodle.contentSelected()}
                  className="p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
                  style={{ color: accentColor }}
                >
                  <X size={22} />
                </button>
              </div>
            )}
            {/* Entrada al composer de anuncios — gateada server-side por canBroadcast */}
            {canBroadcast && (
              <div className="px-4 pt-4">
                <button
                  onClick={() => setShowComposer(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-lg text-white transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  style={{ backgroundColor: accentColor }}
                >
                  <Megaphone size={18} />
                  Nuevo anuncio
                </button>
              </div>
            )}

            {/* Entrada al composer de grupos — gateada server-side por canCreateGroup */}
            {canCreateGroup && (
              <div className="px-4 pt-2">
                <button
                  onClick={() => setShowGroupComposer(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-lg text-white transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Users size={18} />
                  Nuevo grupo
                </button>
              </div>
            )}

            {/* Caja de Búsqueda */}
            <div className="p-4 border-b transition-colors duration-300" style={{ borderColor: borderCol }}>
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="Buscar contacto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className={`w-full pl-12 pr-12 py-3 rounded-full text-xl font-medium focus:outline-none transition-all duration-300 ${
                    isDark 
                      ? 'bg-slate-800/80 text-white placeholder-slate-500 focus:bg-slate-800 focus:ring-1 focus:ring-slate-600' 
                      : 'bg-slate-100 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-slate-300'
                  }`}
                />
                <Search 
                  size={20} 
                  className="absolute left-4.5 pointer-events-none transition-colors duration-300"
                  style={{ color: isSearchFocused ? accentColor : '#94a3b8' }}
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4.5 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Listado */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
              {loading && contacts.length === 0 && broadcastConversations.length === 0 && groupConversations.length === 0 ? (
                <div className="flex flex-col justify-center items-center py-16 space-y-3">
                  <CircularProgress size={30} style={{ color: primaryColor }} />
                  <span className="text-xl text-slate-400">Cargando contactos...</span>
                </div>
              ) : contacts.length === 0 && broadcastConversations.length === 0 && groupConversations.length === 0 ? (
                <div className="text-center text-xl text-slate-400 py-16 px-4 italic">
                  No hay contactos disponibles en este curso.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Sección Anuncios — hilos de broadcast de solo lectura */}
                  {broadcastConversations.length > 0 && (
                    <ChatSection
                      title="Anuncios"
                      icon={<Megaphone size={16} />}
                      count={broadcastConversations.length}
                      unreadCount={broadcastUnread}
                      isOpen={openSections.broadcasts}
                      onToggle={() => toggleSection('broadcasts')}
                      accentColor={accentColor}
                      isDark={isDark}
                    >
                      <div className="space-y-2">
                        {broadcastConversations.map(group => (
                          <GroupRow
                            key={group.id}
                            group={group}
                            onClick={() => selectContact(group)}
                            isDark={isDark}
                            primaryColor={primaryColor}
                          />
                        ))}
                      </div>
                    </ChatSection>
                  )}

                  {/* Sección Grupos — chats bidireccionales curados por el docente */}
                  {groupConversations.length > 0 && (
                    <ChatSection
                      title="Grupos"
                      icon={<Users size={16} />}
                      count={groupConversations.length}
                      unreadCount={groupUnread}
                      isOpen={openSections.groups}
                      onToggle={() => toggleSection('groups')}
                      accentColor={primaryColor}
                      isDark={isDark}
                      action={
                        canCreateGroup ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowGroupComposer(true);
                            }}
                            title="Crear grupo"
                            className="p-1 rounded-full hover:bg-slate-200/70 dark:hover:bg-slate-700/50 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors bg-transparent border-0 cursor-pointer"
                          >
                            <Plus size={16} />
                          </button>
                        ) : null
                      }
                    >
                      <div className="space-y-2">
                        {groupConversations.map(group => (
                          <GroupRow
                            key={group.id}
                            group={group}
                            onClick={() => selectContact(group)}
                            isDark={isDark}
                            primaryColor={primaryColor}
                            onManage={canCreateGroup ? () => setManagedGroup(group) : null}
                          />
                        ))}
                      </div>
                    </ChatSection>
                  )}

                  {/* Sección Tutores */}
                  {tutors.length > 0 && (
                    <ChatSection
                      title="Docentes / Tutores"
                      icon={<GraduationCap size={16} />}
                      count={tutors.length}
                      unreadCount={tutorsUnread}
                      isOpen={openSections.tutors}
                      onToggle={() => toggleSection('tutors')}
                      accentColor={accentColor}
                      isDark={isDark}
                    >
                      <div className="space-y-2">
                        {tutors.map(contact => (
                          <ContactRow
                            key={contact.id}
                            contact={contact}
                            onClick={() => selectContact(contact)}
                            isDark={isDark}
                            primaryColor={primaryColor}
                            renderAvatar={renderPremiumAvatar}
                          />
                        ))}
                      </div>
                    </ChatSection>
                  )}

                  {/* Sección Compañeros */}
                  {allClassmates.length > 0 && (
                    <ChatSection
                      title="Compañeros"
                      icon={<MessageSquare size={16} />}
                      count={searchTerm ? displayedClassmates.length : allClassmates.length}
                      unreadCount={classmatesUnread}
                      isOpen={openSections.classmates}
                      onToggle={() => toggleSection('classmates')}
                      accentColor={textSecondary}
                      isDark={isDark}
                    >
                      <div className="space-y-2">
                        {displayedClassmates.length > 0 ? (
                          displayedClassmates.map(contact => (
                            <ContactRow
                              key={contact.id}
                              contact={contact}
                              onClick={() => selectContact(contact)}
                              isDark={isDark}
                              primaryColor={primaryColor}
                              renderAvatar={renderPremiumAvatar}
                            />
                          ))
                        ) : (
                          <div className="py-4 text-center text-sm text-slate-400 italic">
                            No hay conversaciones activas con compañeros.
                          </div>
                        )}

                        {/* Botón para expandir/colapsar todos los compañeros cuando no hay búsqueda activa */}
                        {!searchTerm && inactiveClassmates.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAllClassmates(prev => !prev)}
                            className="w-full py-2.5 px-3 mt-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors border border-dashed border-slate-300 dark:border-slate-700 hover:bg-slate-100/70 dark:hover:bg-slate-800/50 cursor-pointer"
                            style={{ color: primaryColor }}
                          >
                            {showAllClassmates ? (
                              <>Mostrar solo chats activos</>
                            ) : (
                              <>
                                <UserPlus size={14} />
                                Ver todos los compañeros ({inactiveClassmates.length} disponibles)
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </ChatSection>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          /* VISTA 2: Conversación Abierta (100% de ancho) */
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col h-full overflow-hidden"
          >
            {/* Header del Chat */}
            <div 
              className="p-4 flex items-center justify-between border-b shadow-sm transition-colors duration-300 animate-fade-in"
              style={{ 
                borderColor: borderCol,
                backgroundColor: isDark ? '#162542' : '#ffffff'
              }}
            >
              <div className="flex items-center gap-3.5">
                {/* Botón Volver */}
                <button 
                  onClick={() => selectContact(null)}
                  className="mr-1 p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
                  style={{ color: accentColor }}
                >
                  <ArrowLeft size={22} />
                </button>

                <div className="relative">
                  {selectedContact.isGroup ? (
                    <Avatar
                      style={{
                        width: 44,
                        height: 44,
                        background: selectedContact.isBroadcast
                          ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`
                          : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                        color: '#ffffff',
                        border: `1.5px solid ${borderCol}`
                      }}
                    >
                      {selectedContact.isBroadcast ? <Megaphone size={20} /> : <Users size={20} />}
                    </Avatar>
                  ) : (
                    renderPremiumAvatar(selectedContact, 44)
                  )}
                  {!selectedContact.isGroup && (
                    <OnlineDot isonline={selectedContact.isonline} isDark={isDark} />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-xl m-0 leading-snug" style={{ color: textPrimary }}>
                    {selectedContact.fullname}
                  </h3>
                  <span
                    className="text-base font-bold"
                    style={{ color: (selectedContact.isGroup && selectedContact.isBroadcast) || selectedContact.istutor ? accentColor : (selectedContact.isGroup ? primaryColor : textSecondary) }}
                  >
                    {selectedContact.isGroup
                      ? (selectedContact.isBroadcast ? 'Anuncio (solo lectura)' : 'Grupo')
                      : (selectedContact.istutor ? 'Docente / Tutor' : 'Estudiante')}
                  </span>
                </div>
              </div>

              {/* Botón Cerrar Modal (Solo en Móvil) */}
              {isMobile && (
                <button 
                  onClick={() => moodle.contentSelected()}
                  className="p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
                  style={{ color: accentColor }}
                >
                  <X size={22} />
                </button>
              )}
            </div>

            {error && (
              <div className="bg-rose-500/10 text-rose-500 p-2.5 text-base font-medium text-center border-b border-rose-500/20">
                Conexión inestable. Sincronizando...
              </div>
            )}

            {/* Lista de Mensajes */}
            <MessageList messages={messages} />

            {/* Input de Mensajes — oculto solo para hilos de anuncios (solo
                lectura). Un grupo bidireccional (kind === 'group') SIEMPRE
                muestra el composer, aunque isGroup también sea truthy. */}
            {!selectedContact.isBroadcast && (
              <MessageInput onSendMessage={handleSendMessage} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GroupRow = ({ group, onClick, isDark, primaryColor, onManage }) => {
  const accentColor = '#fdbf00';
  const rowColor = group.isBroadcast ? accentColor : primaryColor;
  const [menuAnchor, setMenuAnchor] = useState(null);

  const closeMenu = () => setMenuAnchor(null);

  return (
    // A div, not a button: the row holds its own buttons (open + menu), and
    // a button nested inside a button is invalid HTML that browsers silently
    // reflow, breaking the click targets.
    <motion.div
      whileHover={{ scale: 1.01, x: 2 }}
      whileTap={{ scale: 0.99 }}
      className="w-full flex items-center rounded-2xl border border-transparent hover:bg-slate-100/60 dark:hover:bg-slate-800/40 transition-colors duration-200"
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 flex items-center gap-4 p-3.5 text-left bg-transparent border-0"
      >
        <div className="relative">
          <Badge
            color="error"
            badgeContent={group.unreadcount}
            variant="standard"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '11px',
                height: '20px',
                minWidth: '20px',
                padding: '0 4px',
                backgroundColor: '#ef4444',
                fontWeight: 'bold',
              }
            }}
          >
            <Avatar
              style={{
                width: 44,
                height: 44,
                background: `linear-gradient(135deg, ${rowColor} 0%, ${rowColor}dd 100%)`,
                color: '#ffffff',
              }}
            >
              {group.isBroadcast ? <Megaphone size={20} /> : <Users size={20} />}
            </Avatar>
          </Badge>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-xl truncate m-0 leading-tight" style={{ color: isDark ? '#e2e8f0' : '#475569' }}>
            {group.fullname}
          </p>
          <p className="text-base text-slate-400 truncate m-0 mt-1 leading-none font-medium">
            {group.isBroadcast ? 'Anuncio (solo lectura)' : 'Grupo'}
          </p>
        </div>

        {/* Dentro del botón a propósito: la flecha es parte del área que abre
            la conversación, como lo era cuando la fila entera era un botón. */}
        <ChevronRight
          size={20}
          data-testid="group-row-arrow"
          className="text-slate-400 opacity-60 shrink-0"
        />
      </button>

      {/* PARCHE / ALCANCE PARCIAL: este menú existe para alojar las acciones
          de gestión de la conversación, pero por ahora solo ofrece "Editar".
          Falta "Eliminar" (no hay endpoint de borrado en el plugin) y el menú
          no se muestra en anuncios (broadcast no tiene ninguna operación de
          gestión implementada). Ver decisions/group-broadcast-management-menu. */}
      {onManage && (
        <>
          <button
            type="button"
            aria-label="Opciones del grupo"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            className="p-2 mr-1 rounded-full hover:bg-slate-200/70 dark:hover:bg-slate-700/50 text-slate-400 hover:text-slate-600 transition-colors bg-transparent border-0"
          >
            <MoreVertical size={20} />
          </button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            // El panel de chat es z-[9999] en mobile y el portal de MUI sale
            // con z-index 1300, así que sin esto el menú abre DETRÁS del panel
            // y parece que el botón no hace nada.
            sx={{ zIndex: 10000 }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                onManage();
              }}
            >
              <Pencil size={16} style={{ marginRight: 10 }} />
              Editar grupo
            </MenuItem>
          </Menu>
        </>
      )}
    </motion.div>
  );
};

const ContactRow = ({ contact, onClick, isDark, primaryColor, renderAvatar }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.01, x: 2 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className="w-full flex items-center gap-4 p-3.5 rounded-2xl text-left border border-transparent hover:bg-slate-100/60 dark:hover:bg-slate-800/40 transition-colors duration-200"
    >
      <div className="relative">
        <Badge
          color="error"
          badgeContent={contact.unreadcount}
          variant="standard"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '11px',
              height: '20px',
              minWidth: '20px',
              padding: '0 4px',
              backgroundColor: '#ef4444',
              fontWeight: 'bold',
            }
          }}
        >
          {renderAvatar(contact, 44)}
        </Badge>
        <OnlineDot isonline={contact.isonline} isDark={isDark} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-xl truncate m-0 leading-tight" style={{ color: isDark ? '#e2e8f0' : '#475569' }}>
          {contact.fullname}
        </p>
        <p className="text-base text-slate-400 truncate m-0 mt-1 leading-none font-medium">
          {contact.istutor ? 'Docente' : 'Estudiante'}
        </p>
      </div>

      <ChevronRight
        size={20}
        className="text-slate-400 opacity-60"
      />
    </motion.button>
  );
};

// Renders only when the backend actually reports presence; a plugin version
// without `isonline` simply renders nothing instead of a misleading grey dot.
const OnlineDot = ({ isonline, isDark }) => {
  if (typeof isonline !== 'boolean' || !isonline) {
    return null;
  }
  return (
    <span
      aria-label="En línea"
      title="En línea"
      className="absolute bottom-0 right-0 rounded-full"
      style={{
        width: 12,
        height: 12,
        backgroundColor: '#22c55e',
        border: `2px solid ${isDark ? '#0f172a' : '#ffffff'}`,
      }}
    />
  );
};

export default ChatContainer;
